import { Link } from 'react-router-dom';
import { CheckCircle, Package, Shield, Clock, ArrowRight } from 'lucide-react';

const Home = () => {
  return (
    <div className="font-inter" data-testid="home-page">
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center h-[600px] flex items-center"
        style={{ 
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(/assets/images/home-hero.jpg)',
          backgroundPosition: 'center'
        }}
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <h1 className="font-poppins font-bold text-4xl md:text-5xl lg:text-6xl mb-6 leading-tight" data-testid="hero-title">
            Wooden Pallet Manufacturing<br />Company in India
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-200 font-medium" data-testid="hero-subtitle">
            ISPM-15 Certified | Custom Wooden Pallets | Export-Ready Packaging
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/contact"
              className="bg-[#B11217] text-white px-8 py-4 font-poppins font-semibold text-sm uppercase tracking-wide hover:bg-[#8b0e12] transition-colors inline-block text-center"
              data-testid="hero-cta-primary"
            >
              Get a Quote
            </Link>
            <Link
              to="/products"
              className="bg-white text-[#2B2B2B] px-8 py-4 font-poppins font-semibold text-sm uppercase tracking-wide hover:bg-gray-100 transition-colors inline-block text-center"
              data-testid="hero-cta-secondary"
            >
              View Products
            </Link>
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-16 bg-[#F2F2F2]" data-testid="product-categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to="/products/wooden-pallets"
              className="group bg-white p-8 hover:shadow-xl transition-all duration-300 border-l-4 border-[#B11217]"
              data-testid="category-wooden-pallets"
            >
              <h3 className="font-poppins font-bold text-2xl text-[#2B2B2B] mb-3 group-hover:text-[#B11217] transition-colors">
                Wooden Pallets
              </h3>
              <p className="text-gray-600 mb-4">
                Pine Wood, Hardwood, Jungle Wood pallets in custom sizes. ISPM-15 heat treated for export.
              </p>
              <span className="inline-flex items-center text-[#B11217] font-semibold group-hover:gap-2 transition-all">
                Learn More <ArrowRight size={18} className="ml-1" />
              </span>
            </Link>

            <Link
              to="/products/industrial-packaging"
              className="group bg-white p-8 hover:shadow-xl transition-all duration-300 border-l-4 border-[#B11217]"
              data-testid="category-industrial-packaging"
            >
              <h3 className="font-poppins font-bold text-2xl text-[#2B2B2B] mb-3 group-hover:text-[#B11217] transition-colors">
                Export Packaging
              </h3>
              <p className="text-gray-600 mb-4">
                Wooden boxes, crates, plywood packaging, and corrugated solutions for safe export shipping.
              </p>
              <span className="inline-flex items-center text-[#B11217] font-semibold group-hover:gap-2 transition-all">
                Learn More <ArrowRight size={18} className="ml-1" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Why SMG */}
      <section className="py-16 bg-white" data-testid="why-smg-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-[#2B2B2B] mb-4">
              Why Choose <span className="text-[#B11217]">Sita Mani Global</span>
            </h2>
            <div className="w-24 h-1 bg-[#B11217] mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center" data-testid="feature-ispm15">
              <div className="w-20 h-20 bg-[#B11217] rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield size={40} className="text-white" />
              </div>
              <h3 className="font-poppins font-bold text-xl text-[#2B2B2B] mb-3">ISPM-15 Certified</h3>
              <p className="text-gray-600">
                Heat treated and fumigated pallets meeting international export standards.
              </p>
            </div>

            <div className="text-center" data-testid="feature-custom">
              <div className="w-20 h-20 bg-[#B11217] rounded-full flex items-center justify-center mx-auto mb-4">
                <Package size={40} className="text-white" />
              </div>
              <h3 className="font-poppins font-bold text-xl text-[#2B2B2B] mb-3">Custom Solutions</h3>
              <p className="text-gray-600">
                Tailored sizes and load capacities to match your specific requirements.
              </p>
            </div>

            <div className="text-center" data-testid="feature-bulk">
              <div className="w-20 h-20 bg-[#B11217] rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={40} className="text-white" />
              </div>
              <h3 className="font-poppins font-bold text-xl text-[#2B2B2B] mb-3">Bulk Manufacturing</h3>
              <p className="text-gray-600">
                Large-scale production capacity for high-volume orders.
              </p>
            </div>

            <div className="text-center" data-testid="feature-delivery">
              <div className="w-20 h-20 bg-[#B11217] rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock size={40} className="text-white" />
              </div>
              <h3 className="font-poppins font-bold text-xl text-[#2B2B2B] mb-3">On-Time Delivery</h3>
              <p className="text-gray-600">
                Reliable logistics and timely fulfillment for your business needs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-16 bg-[#F2F2F2]" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-[#2B2B2B] mb-4">
              Industries We Serve
            </h2>
            <div className="w-24 h-1 bg-[#B11217] mx-auto"></div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {['Warehousing & Logistics', 'FMCG', 'Pharmaceutical', 'Exporters', 'Heavy Manufacturing'].map((industry) => (
              <div
                key={industry}
                className="bg-white p-6 text-center hover:shadow-lg transition-shadow"
                data-testid={`industry-${industry.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <p className="font-poppins font-semibold text-[#2B2B2B]">{industry}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/industries"
              className="inline-flex items-center text-[#B11217] font-semibold hover:gap-2 transition-all"
              data-testid="view-all-industries"
            >
              View All Industries <ArrowRight size={18} className="ml-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Band */}
      <section className="bg-[#B11217] py-16" data-testid="cta-band">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="font-poppins font-bold text-3xl md:text-4xl mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 text-gray-100">
            Contact us today for custom wooden pallet solutions and export packaging.
          </p>
          <Link
            to="/contact"
            className="bg-white text-[#B11217] px-8 py-4 font-poppins font-semibold text-sm uppercase tracking-wide hover:bg-gray-100 transition-colors inline-block"
            data-testid="cta-band-button"
          >
            Request a Quote
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;